import { eq, and, isNotNull } from 'drizzle-orm'
import { inngest } from '@/lib/inngest'
import { db as defaultDb } from '@/db'
import { tickets, ticketMessages, aiArtifacts, tenantConfigs, auditLog, knowledgeEntries } from '@/db/schema'
import { buildTagSuggestionPrompt } from '../../prompts/tag-suggestion'
import { callTagSuggestionLLM, type TagSuggestionResult, type AIGatewayConfig } from '@/lib/ai-gateway'
import type { PostgresJsDatabase } from 'drizzle-orm/postgres-js'
import type * as schema from '@/db/schema'

type DB = PostgresJsDatabase<typeof schema>

export type SuggestTagsInput = {
  ticketId: string
  tenantId: string
  messageId: string
  ticketVersion: number
}

export async function runTagSuggestion(
  input: SuggestTagsInput,
  options?: {
    db?: DB
    callLLM?: (config: AIGatewayConfig, system: string, user: string) => Promise<TagSuggestionResult>
  },
): Promise<{ outcome: 'tagged' | 'stale' | 'degraded'; tags?: string[] }> {
  const db = (options?.db ?? defaultDb) as DB
  const llm = options?.callLLM ?? callTagSuggestionLLM

  const [ticket] = await db
    .select()
    .from(tickets)
    .where(and(eq(tickets.id, input.ticketId), eq(tickets.tenantId, input.tenantId)))
    .limit(1)

  if (!ticket) throw new Error(`Ticket not found: ${input.ticketId}`)

  // Duplicate guard — skip if already tagged for this trigger message
  const [existing] = await db
    .select({ id: aiArtifacts.id })
    .from(aiArtifacts)
    .where(and(
      eq(aiArtifacts.ticketId, input.ticketId),
      eq(aiArtifacts.artifactType, 'tag_suggestion'),
      eq(aiArtifacts.sourceMessageId, input.messageId),
    ))
    .limit(1)

  if (existing) return { outcome: 'stale' }

  const [config] = await db
    .select()
    .from(tenantConfigs)
    .where(eq(tenantConfigs.tenantId, input.tenantId))
    .limit(1)

  if (!config) throw new Error(`Tenant config not found: ${input.tenantId}`)

  // Fetch KB vocabulary (up to 30 entries by slug for tag grounding)
  const kbEntries = await db
    .select({ title: knowledgeEntries.title, slug: knowledgeEntries.slug })
    .from(knowledgeEntries)
    .where(and(eq(knowledgeEntries.tenantId, input.tenantId), isNotNull(knowledgeEntries.embedding), eq(knowledgeEntries.isDeleted, false)))
    .limit(30)

  const [triggerMessage] = await db
    .select({ bodySanitized: ticketMessages.bodySanitized })
    .from(ticketMessages)
    .where(eq(ticketMessages.id, input.messageId))
    .limit(1)

  const { system, user } = buildTagSuggestionPrompt({
    referenceNumber: ticket.referenceNumber,
    submissionText: triggerMessage?.bodySanitized ?? '',
    issueCategory: ticket.issueCategory ?? undefined,
    affectedSystem: ticket.affectedSystem ?? undefined,
    kbVocabulary: kbEntries,
  })

  let result: TagSuggestionResult
  try {
    result = await llm(
      {
        provider: config.aiProvider,
        model: config.aiModel,
        temperature: config.aiTemperature,
        dataProcessingConsent: config.dataProcessingConsent,
      },
      system,
      user,
    )
  } catch (err) {
    await db.insert(auditLog).values({
      tenantId: input.tenantId,
      actorId: null,
      actorType: 'ai_agent',
      action: 'ticket.ai_degraded',
      entityType: 'ticket',
      entityId: input.ticketId,
      payload: { hook: 'tag-suggestion', error: err instanceof Error ? err.message : 'Unknown error' },
    })
    return { outcome: 'degraded' }
  }

  // Normalise tags: lowercase, trimmed, max 5
  const tags = result.tags
    .map((t) => t.toLowerCase().trim())
    .filter((t) => t.length > 0)
    .slice(0, 5)

  await db.insert(aiArtifacts).values({
    tenantId: input.tenantId,
    ticketId: input.ticketId,
    sourceMessageId: input.messageId,
    triggerEvent: 'ticket/triaged',
    artifactType: 'tag_suggestion',
    content: tags.join(', '),
    contentJson: JSON.stringify({ tags, reasoning: result.reasoning }),
    modelUsed: config.aiModel,
    promptVersion: '1.0.0',
    validationStatus: 'valid',
  })

  // Update ticket tags — merge AI suggestions with any existing manual tags
  const merged = [...new Set([...(ticket.categoryTags ?? []), ...tags])]

  await db
    .update(tickets)
    .set({ categoryTags: merged, updatedAt: new Date() })
    .where(eq(tickets.id, input.ticketId))

  await db.insert(auditLog).values({
    tenantId: input.tenantId,
    actorId: null,
    actorType: 'ai_agent',
    action: 'ticket.tags_suggested',
    entityType: 'ticket',
    entityId: input.ticketId,
    payload: { tags },
  })

  return { outcome: 'tagged', tags }
}

export const taggingAgentFn = inngest.createFunction(
  { id: 'auto-tagging', retries: 3, triggers: [{ event: 'ticket/triaged' }] },
  async ({ event, step }) => {
    return await step.run('run-tag-suggestion', () =>
      runTagSuggestion(event.data as SuggestTagsInput),
    )
  },
)
